import React from 'react';
import { CheckCircle, FileText, Shield, Award, Clock, TrendingUp } from 'lucide-react';
import ServiceFAQ from '../../components/ServiceFAQ';

const NonProfitLaw = ({ onGetStarted }) => {
  const benefits = [
    'Tax-exempt status application and maintenance',
    'Corporate governance and compliance',
    'Fundraising and donor relations',
    'Employment and volunteer matters',
    'Charitable solicitation registration',
    'Board training and organizational structuring',
  ];

  const process = [
    { title: 'Formation & Registration', description: 'Establish non-profit entity and obtain tax-exempt status.' },
    { title: 'Governance Setup', description: 'Implement board structure and operational policies.' },
    { title: 'Compliance Planning', description: 'Develop ongoing compliance procedures and reporting.' },
    { title: 'Ongoing Support', description: 'Provide continuous legal guidance as organization grows.' },
  ];

  const serviceAreas = [
    '501(c)(3) Formation',
    'Tax-Exempt Status',
    'Board Governance',
    'Grant Compliance',
    'Fundraising Regulations',
    'Employment Matters',
    'Program Development',
    'Charitable Solicitation',
  ];

  const whyChoose = [
    { icon: Award, title: 'Non-Profit Expertise', description: 'Specialized knowledge of non-profit law and regulations.' },
    { icon: Shield, title: 'Compliance Focus', description: 'Ensure adherence to complex tax-exempt requirements.' },
    { icon: Clock, title: 'Efficient Processing', description: 'Streamlined formation and IRS application process.' },
    { icon: TrendingUp, title: 'Mission Support', description: 'Legal strategies aligned with your charitable mission.' },
  ];

  const faqs = [
    { question: 'What is a 501(c)(3) organization?', answer: 'A 501(c)(3) is a tax-exempt non-profit organization recognized by the IRS for charitable, religious, educational, scientific, or literary purposes. Donations to 501(c)(3) organizations are tax-deductible for donors, making them attractive for fundraising.' },
    { question: 'How long does it take to obtain tax-exempt status?', answer: 'The IRS typically processes Form 1023 applications in several months to over a year. Form 1023-EZ (streamlined application) is processed faster, often within weeks. We expedite the process by ensuring complete, accurate applications.' },
    { question: 'What are the ongoing compliance requirements?', answer: 'Non-profits must file annual returns (Form 990), maintain proper corporate governance, avoid private benefit or inurement, comply with state registration requirements, and adhere to fundraising regulations. We help establish systems for ongoing compliance.' },
    { question: 'Can non-profit board members be compensated?', answer: 'Board members typically serve without compensation, though they may be reimbursed for expenses. Non-profit employees (including executive directors) can receive reasonable compensation. The key is ensuring compensation is reasonable and properly documented.' },
    { question: 'What is the difference between 501(c)(3) and 501(c)(4)?', answer: '501(c)(3) organizations are charitable and donations are tax-deductible, but political activity is restricted. 501(c)(4) organizations are social welfare organizations that can engage in more political activity, but donations are not tax-deductible.' },
  ];

  return (
    <div className="pt-20">
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7156168/pexels-photo-7156168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/80"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">Business Law Services</div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6">Non-Profit Law</h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8">Formation, compliance, and governance for non-profit organizations. We help charities achieve their mission while maintaining legal compliance.</p>
            <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Started</button>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-6">Overview</h2>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80 mb-6">Non-profit organizations face unique legal requirements from formation through daily operations. Our attorneys provide comprehensive legal support for tax-exempt organizations, ensuring compliance with federal and state regulations.</p>
              <p className="text-base leading-relaxed text-[#2E2E2E]/80">We help non-profits focus on their mission while maintaining legal compliance.</p>
              <h3 className="text-xl lg:text-2xl text-[#2C3E50] font-semibold mb-6 mt-8">Key Benefits</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#2C3E50] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#2E2E2E]/80">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img src="https://images.pexels.com/photos/7156162/pexels-photo-7156162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Non-profit community support" className="w-full h-[450px] object-cover shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">Our Non-Profit Services</h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">Comprehensive legal services for tax-exempt organizations.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mobile-swiper">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6 hover:border-[#2C3E50]/20 transition-all">
                <FileText className="w-8 h-8 text-[#CA9A67] mb-3" strokeWidth={1.5} />
                <h4 className="font-semibold text-[#2C3E50]">{area}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundImage: 'url(https://images.pexels.com/photos/5668858/pexels-photo-5668858.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <div className="absolute inset-0 bg-[#2C3E50]/90"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-[#CA9A67] mb-4">Our Approach</div>
            <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-4">Our Process</h2>
            <p className="text-base leading-relaxed text-white/70 max-w-2xl mx-auto">A structured approach to non-profit legal matters.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mobile-swiper">
            {process.map((step, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl font-bold text-[#CA9A67] mb-4">{index + 1}</div>
                <h3 className="text-xl text-white font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://images.pexels.com/photos/6647042/pexels-photo-6647042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Why choose our non-profit team" className="w-full h-[500px] object-cover shadow-lg" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-8">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mobile-swiper">
                {whyChoose.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="bg-white border border-[#2C3E50]/5 shadow-sm p-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-[#CA9A67] mb-4">
                        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                      </div>
                      <h3 className="text-lg text-[#2C3E50] font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/6646917/pexels-photo-6646917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">Ready to Start Your Non-Profit?</h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">Contact us for expert non-profit legal services.</p>
          <button onClick={onGetStarted} className="bg-[#CA9A67] text-white hover:bg-[#b8894f] transition-colors px-8 py-4 font-medium">Get Legal Help Now</button>
        </div>
      </section>
    </div>
  );
};

export default NonProfitLaw;
